/**
 * Font library with Google Fonts and system font definitions.
 * Provides utilities for loading, filtering, and searching fonts.
 */

export interface FontDef {
    family: string
    category: 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'
    weights: number[]
    isSystem?: boolean
}

export const FONT_LIST: FontDef[] = [
    // ── System fonts ────────────────────────────────────────────────────────────
    { family: 'Arial', category: 'sans-serif', weights: [400, 700], isSystem: true },
    { family: 'Helvetica', category: 'sans-serif', weights: [400, 700], isSystem: true },
    { family: 'Georgia', category: 'serif', weights: [400, 700], isSystem: true },
    { family: 'Times New Roman', category: 'serif', weights: [400, 700], isSystem: true },
    { family: 'Courier New', category: 'monospace', weights: [400, 700], isSystem: true },
    { family: 'Verdana', category: 'sans-serif', weights: [400, 700], isSystem: true },

    // ── Sans-serif ───────────────────────────────────────────────────────────────
    { family: 'Inter', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Roboto', category: 'sans-serif', weights: [300, 400, 500, 700] },
    { family: 'Open Sans', category: 'sans-serif', weights: [300, 400, 600, 700, 800] },
    { family: 'Lato', category: 'sans-serif', weights: [300, 400, 700] },
    { family: 'Montserrat', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Poppins', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Nunito', category: 'sans-serif', weights: [300, 400, 600, 700, 800] },
    { family: 'Raleway', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Work Sans', category: 'sans-serif', weights: [300, 400, 500, 600, 700] },
    { family: 'DM Sans', category: 'sans-serif', weights: [300, 400, 500, 700] },
    { family: 'Plus Jakarta Sans', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Outfit', category: 'sans-serif', weights: [300, 400, 500, 600, 700] },
    { family: 'Figtree', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },

    // ── Serif ────────────────────────────────────────────────────────────────────
    { family: 'Playfair Display', category: 'serif', weights: [400, 500, 600, 700, 800] },
    { family: 'Merriweather', category: 'serif', weights: [300, 400, 700] },
    { family: 'Lora', category: 'serif', weights: [400, 500, 600, 700] },
    { family: 'Libre Baskerville', category: 'serif', weights: [400, 700] },
    { family: 'EB Garamond', category: 'serif', weights: [400, 500, 600, 700, 800] },
    { family: 'Crimson Text', category: 'serif', weights: [400, 600, 700] },
    { family: 'Noto Serif', category: 'serif', weights: [400, 700] },

    // ── Display ──────────────────────────────────────────────────────────────────
    { family: 'Oswald', category: 'display', weights: [300, 400, 500, 600, 700] },
    { family: 'Bebas Neue', category: 'display', weights: [400] },
    { family: 'Anton', category: 'display', weights: [400] },
    { family: 'Archivo Black', category: 'display', weights: [400] },
    { family: 'Righteous', category: 'display', weights: [400] },
    { family: 'Permanent Marker', category: 'display', weights: [400] },

    // ── Handwriting ──────────────────────────────────────────────────────────────
    { family: 'Dancing Script', category: 'handwriting', weights: [400, 500, 600, 700] },
    { family: 'Pacifico', category: 'handwriting', weights: [400] },
    { family: 'Caveat', category: 'handwriting', weights: [400, 500, 600, 700] },
    { family: 'Satisfy', category: 'handwriting', weights: [400] },
    { family: 'Great Vibes', category: 'handwriting', weights: [400] },
    { family: 'Sacramento', category: 'handwriting', weights: [400] },

    // ── Monospace ────────────────────────────────────────────────────────────────
    { family: 'JetBrains Mono', category: 'monospace', weights: [400, 700] },
    { family: 'Fira Code', category: 'monospace', weights: [300, 400, 500, 600, 700] },
    { family: 'Source Code Pro', category: 'monospace', weights: [300, 400, 500, 600, 700] },
    { family: 'IBM Plex Mono', category: 'monospace', weights: [300, 400, 500, 600, 700] },
]

/** Tracks which Google Font families have already been injected into the document. */
const _loadedFonts = new Set<string>()

/**
 * Loads a Google Font by injecting a <link> element into the document head.
 * System fonts are skipped. Waits for `document.fonts.ready` after injection.
 */
export async function loadFont(family: string): Promise<void> {
    const def = FONT_LIST.find((f) => f.family === family)
    if (!def) return

    // System fonts are always available — nothing to load
    if (def.isSystem) return

    // Avoid duplicate <link> injections
    if (_loadedFonts.has(family)) return

    const weights = def.weights.join(';')
    const encoded = encodeURIComponent(family)
    const href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@${weights}&display=swap`

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    document.head.appendChild(link)

    _loadedFonts.add(family)

    await document.fonts.ready
}

/**
 * Returns fonts filtered by category.
 * If no category is provided, returns all fonts.
 */
export function getFontsByCategory(category?: string): FontDef[] {
    if (!category) return FONT_LIST
    return FONT_LIST.filter((f) => f.category === category)
}

/**
 * Returns fonts whose family name contains the query string (case-insensitive).
 */
export function searchFonts(query: string): FontDef[] {
    const lower = query.toLowerCase().trim()
    if (!lower) return FONT_LIST
    return FONT_LIST.filter((f) => f.family.toLowerCase().includes(lower))
}
